/**
 * 🔴 **THE APPLICATION'S OWN SIGN-IN** (D53) — `POST /auth/sign-in` on Pinksy, not the Passport.
 *
 * Devin: *"let's get the application up and running, and then let's enhance it with the passport…
 * we'll start bringing in the passport once we've created a user identity."*
 *
 * Deliberately NOT `authFetch`: there is no session yet, and this is the request that makes one.
 */
import { API_BASE_URL } from "@/api/base";
import { deviceId } from "./device";
import { IDENTITY_BASE } from "./identity";
import type { FetchLike } from "./http";

export const SIGN_IN_PATH = "/auth/sign-in";

/**
 * 🔴 **THE SECOND SESSION, AND WHY A PASSWORD SIGN-IN HAS TO ESTABLISH IT TOO** (PNK-148).
 *
 * This product runs two session systems, by design: Pinksy's own JWT (`/auth/sign-in`, D53/D83)
 * and Better Auth's cookie (`/identity/*`, D84). Everything the app does uses the first. Passkeys
 * use the second, because they ARE Better Auth — `auth/passkey.ts` calls those routes with
 * `credentials: "include"` and no bearer, since the `bearer` plugin was deliberately removed (D89).
 *
 * `POST /auth/sign-in` returns a token and sets NO cookie. Measured: `/auth/sign-in` → 200 with no
 * `Set-Cookie`; `/identity/sign-in/email` → 200 with `better-auth.session_token`. So a person who
 * signed in with a password held no Better Auth session at all, and every passkey route answered
 * 401 — *"could not start passkey setup"*, in production, for the entire population the enrolment
 * screen exists to serve. Only a Google sign-in worked, because its OAuth callback sets the cookie.
 * Found by driving enrolment in a real browser; no test saw it, because they all mock this module.
 *
 * 🔴 **AFTER, NEVER INSTEAD, AND NEVER IN PARALLEL.** Pinksy's endpoint is the authority on whether
 * the password is right: it holds the rate limiter, the equal-time refusal that keeps the
 * enumeration oracle shut, and the D83 device/refresh semantics. This runs only once that has
 * ALREADY said yes, which is what keeps a wrong password costing ONE scrypt rather than two — the
 * failing case is the one an attacker drives, and it is the one that must not get more expensive.
 */
export const IDENTITY_SIGN_IN_PATH = `${IDENTITY_BASE}/sign-in/email`;

/** Long enough for a scrypt verification on a loaded server, short enough that a hung request is not a hung screen. */
export const SIGN_IN_TIMEOUT_MS = 20_000;

export interface SignedIn {
  ok: true;
  token: string;
  /**
   * 🔴 **WHAT KEEPS THE SESSION ALIVE** (D83). The access token above lasts fifteen minutes; this is
   * the opaque, single-use credential `renew()` spends for the next pair.
   */
  refreshToken: string | null;
  /** ISO 8601 — when the person must sign in again. 24 hours, or 3 days on a machine they have used. */
  expiresAt: string | null;
  userId: string;
  displayName: string | null;
  email: string;
}

export interface SignInFailed {
  ok: false;
  message: string;
}

export type SignInOutcome = SignedIn | SignInFailed;

interface SignInBody {
  token?: unknown;
  refreshToken?: unknown;
  expiresAt?: unknown;
  userId?: unknown;
  displayName?: unknown;
  email?: unknown;
}

interface ErrorBody {
  message?: unknown;
}

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() !== "" ? value.trim() : null);

/**
 * 🔴 The server's own sentence is passed through UNCHANGED on a refusal.
 *
 * It says the same thing for a wrong password and for an address nobody has, on purpose (sign-in.ts):
 * a client that helpfully rewrote it into "no such user" would hand back the membership oracle the
 * endpoint was written to withhold.
 */
export async function signInWithPassword(email: string, password: string, base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<SignInOutcome> {
  // 🔴 The device id is sent, never demanded (D83): a browser that cannot keep one gets a 24-hour
  // session, which is the same session everybody gets on a machine we have not seen before.
  const device = deviceId();
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), SIGN_IN_TIMEOUT_MS);
  const send = fetchImpl ?? fetch;
  try {
    const response = await send(`${base}${SIGN_IN_PATH}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(device === null ? { email, password } : { email, password, deviceId: device }),
      signal: abort.signal,
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as ErrorBody;
      return { ok: false, message: text(body.message) ?? "sign-in was refused" };
    }
    const body = (await response.json()) as SignInBody;
    const token = text(body.token);
    if (token === null) return { ok: false, message: "the server answered without a session" };
    await establishIdentitySession(email, password, base, send);
    return {
      ok: true,
      token,
      // Absent is honest, not an error: a server without D83 yet simply gives a session that lapses
      // at `exp`, which is what every session did before this.
      refreshToken: text(body.refreshToken),
      expiresAt: text(body.expiresAt),
      userId: text(body.userId) ?? "",
      displayName: text(body.displayName),
      email: text(body.email) ?? email,
    };
  } catch (error) {
    // An abort and a dead connection read the same to a person: the app could not reach the server.
    return { ok: false, message: (error as Error)?.name === "AbortError" ? "the server did not answer in time" : "could not reach the server" };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Mint the Better Auth cookie session that the passkey routes authenticate with.
 *
 * 🔴 **ITS FAILURE IS NOT A SIGN-IN FAILURE.** The person IS signed in — Pinksy's endpoint said so
 * and issued the token this app runs on. If this call fails, the only thing they cannot do is
 * manage passkeys, and turning that into "sign-in was refused" would take a working session away
 * over a feature they may never touch. It degrades and stays quiet; the security screen already
 * says plainly when it cannot read your sign-in methods.
 */
async function establishIdentitySession(email: string, password: string, base: string, send: FetchLike): Promise<void> {
  try {
    await send(`${base}${IDENTITY_SIGN_IN_PATH}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      // 🔴 The cookie is the entire point of the call; without this the response sets nothing.
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
  } catch {
    // Deliberately swallowed — see the note above.
  }
}
